import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Button } from '@iwoca/orion';

import * as selectors from '../../../accountsSelector';

import { GET_REPAYMENT_SCHEDULE } from '../../../../drawDown/DrawDown.actions';

import styles from '../DashboardActionPanel.module.css';
import messages from '../DashboardActionPanel.messages';
import { MoneyNoDecimals } from '../../../../ui/moneyNoDecimals/MoneyNoDecimals';
import RepaymentScheduleSummary from '../../../dashboardComponents/repaymentScheduleSummary/RepaymentScheduleSummary';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { RootState } from '@iwProvider/configureStore';
import { Dispatch } from 'redux';
import { TProductType } from '../../../../../myAccount/dashboard/Dashboard.types';
import { REQUEST_CHANGE_ROUTER } from '@iwRouter/IwRouter.actions';
import { buildLocationWithSearch } from '../../../../../myAccount/drawDown/DrawDownHelper';
import { MIN_DRAWDOWN_AMOUNT } from '../../../../minDrawdownAmount';
import { isMobileWidth } from '../../../../../iwMedia/BreakpointHelper';
import { Text } from '@ui';
import { tracking } from '@iwoca/frontend-tracking-library';
import { ChooseLoanAmount } from '../../../../sharedDashboardComponents/slider/chooseLoanAmount/ChooseLoanAmount';

const mapStateToProps = (state: RootState) => {
    // TODO: Stop relying on the URL for the product type.
    let productType: TProductType = selectors.getProductTypeFromURL();
    if (getCountry() === 'DE' && productType === 'flexi24') {
        productType = 'credit_facility';
    }

    return {
        availableCash: selectors.getMaxDrawDownAmountToTwoDecimalPlaces(
            state,
            productType
        ),
        productType,
        offerId: selectors.getDrawdownOfferId(state, productType),
        isInterestOnlyLoan: selectors.isInterestOnlyLoan(state, productType)
    };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    continueToDrawdown: (amount: number, offerId: string) => {
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            //@ts-ignore
            location: buildLocationWithSearch({
                pathname: '/funding-requirements/',
                offerId: offerId,
                amount: amount
            })
        });
    },
    getRepaymentSchedule: (
        amount: number,
        offerId: string,
        productType: string
    ) => {
        dispatch({
            type: GET_REPAYMENT_SCHEDULE,
            amount,
            offerId,
            productType
        });
    }
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

export const TopUpDrawDown = connect(
    mapStateToProps,
    mapDispatchToProps
)(({
    availableCash,
    productType,
    getRepaymentSchedule,
    continueToDrawdown,
    offerId,
    isInterestOnlyLoan
}: PropsFromRedux) => {
    const [topupDrawdownAmount, setTopupDrawdownAmount] =
        useState(availableCash);

    useEffect(() => {
        getRepaymentSchedule(topupDrawdownAmount, offerId, productType);
    }, [getRepaymentSchedule, offerId, productType, topupDrawdownAmount]);

    return (
        <div className={styles.drawdownContainer}>
            {Boolean(isInterestOnlyLoan) && getCountry() === 'UK' && (
                <Text>
                    Just so you know, when you top up your interest only period
                    will end. You can see what your payments would look like
                    below
                </Text>
            )}
            <ChooseLoanAmount
                drawdownAmount={Math.floor(topupDrawdownAmount)}
                minimumDrawdownAmount={MIN_DRAWDOWN_AMOUNT}
                maximumDrawdownAmount={availableCash}
                setDrawdownAmount={setTopupDrawdownAmount}
                data-testid="drawdown-amount-input"
                label={messages.withdrawMessage}
                showSlider={!isMobileWidth()}
            />

            <Button
                data-testid="continue-to-drawdown-button"
                className={styles.drawdownCTA}
                variant="success"
                onClick={() => {
                    tracking.ampli.drawdownCtaClicked();
                    continueToDrawdown(topupDrawdownAmount, offerId);
                }}
                disabled={
                    topupDrawdownAmount < MIN_DRAWDOWN_AMOUNT ||
                    topupDrawdownAmount > availableCash
                }
            >
                <FormattedMessage
                    {...messages.drawdownCTA}
                    values={{
                        drawdownAmount: (
                            <MoneyNoDecimals
                                value={Math.floor(topupDrawdownAmount)}
                            />
                        )
                    }}
                />
            </Button>
            <RepaymentScheduleSummary
                topupDrawdownAmount={topupDrawdownAmount}
            />
        </div>
    );
});
